<script lang="ts" setup>
import {
  tpgIconAdminPanelSettings,
  tpgIconChat,
  tpgIconFolder,
  tpgIconGroups,
  tpgIconNotifications,
  tpgIconPublics,
  tpgIconSettings,
} from '@tpg/grammar-web-icons';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import {
  GrammarBadge,
  GrammarNavigationRail,
  GrammarNavigationRailItem,
} from '@nora-monorepo/grammar-ui';
import { ConsentStatusEnum } from '@nora-monorepo/nora-api-client';
import { useAppBridgeStore } from '@nora-monorepo/ui-shared';

import { toggleRoute } from '#nora-ui/router/navigation';
import { useRouteFlags } from '#nora-ui/router/route-flags';
import { RouteName } from '#nora-ui/router/route-names';
import { useConsentStore } from '#nora-ui/stores/consent-store';
import { useNotificationStore } from '#nora-ui/stores/notification-store';

const notificationStore = useNotificationStore();

const { numUnreadGeofenceNotifications } = storeToRefs(notificationStore);

const consentStore = useConsentStore();
const appsBridgeStore = useAppBridgeStore();

const { consents } = storeToRefs(consentStore);
const { rootSegment: selectedValue } = useRouteFlags();

const pendingInvitations = computed(() => {
  return consents.value.filter(consent => consent.status === ConsentStatusEnum.SENT).length;
});
</script>

<template>
  <!-- eslint-disable vue/no-deprecated-slot-attribute -->
  <GrammarNavigationRail
    class="flex flex-col gap-5"
    :selected-value="selectedValue"
  >
    <GrammarNavigationRailItem
      slot="top-items"
      v-can="{
        permission: 'view:admin.panel',
      }"
      :icon-template="tpgIconAdminPanelSettings"
      label="Admin"
      value="admin"
      @click="toggleRoute(RouteName.AdminUsers)"
    />
    <GrammarNavigationRailItem
      slot="top-items"
      :icon-template="tpgIconPublics"
      label="Ops"
      value="operations"
      class="max-h-14 overflow-hidden"
      @click="toggleRoute(RouteName.Operations)"
    >
      <template
        v-if="pendingInvitations > 0"
        #badge
      >
        <GrammarBadge
          size="xxs"
          type="accent"
          emphasis="high"
          :label="`${pendingInvitations}`"
        />
      </template>
    </GrammarNavigationRailItem>
    <GrammarNavigationRailItem
      slot="top-items"
      :icon-template="tpgIconChat"
      label="Chat"
      value="chat"
      class="max-h-14 overflow-hidden"
      @click="toggleRoute(RouteName.Chat)"
    >
      <template
        v-if="appsBridgeStore.chat.state.unreadCount > 0"
        #badge
      >
        <GrammarBadge
          size="xxs"
          type="accent"
          emphasis="high"
          :label="appsBridgeStore.chat.state.unreadCount.toString()"
        />
      </template>
    </GrammarNavigationRailItem>
    <GrammarNavigationRailItem
      slot="top-items"
      :icon-template="tpgIconGroups"
      label="Groups"
      value="groups"
      class="max-h-14 overflow-hidden"
      @click="toggleRoute(RouteName.Groups)"
    />
    <GrammarNavigationRailItem
      slot="top-items"
      :icon-template="tpgIconFolder"
      label="Files"
      value="files"
      class="max-h-14 overflow-hidden"
      @click="toggleRoute(RouteName.Files)"
    />
    <GrammarNavigationRailItem
      slot="bottom-items"
      :icon-template="tpgIconNotifications"
      label="Alerts"
      value="notifications"
      class="max-h-14 overflow-hidden"
      @click="toggleRoute(RouteName.Notifications)"
    >
      <template
        v-if="numUnreadGeofenceNotifications > 0"
        #badge
      >
        <GrammarBadge
          size="xxs"
          type="accent"
          emphasis="high"
          :label="`${numUnreadGeofenceNotifications}`"
        />
      </template>
    </GrammarNavigationRailItem>
    <GrammarNavigationRailItem
      slot="bottom-items"
      :icon-template="tpgIconSettings"
      label="Settings"
      value="settings"
      class="max-h-14 overflow-hidden"
      @click="toggleRoute(RouteName.Settings)"
    />
  </GrammarNavigationRail>
</template>
